import * as React from "react";
import _ from "lodash";
import {translate} from "react-i18next";
import {ITranslatedComponent, ITypefamilyMap} from "../../../redux/reducers/generalReducer";

import {Divider, Segment, Table} from "semantic-ui-react";

// services
import {
    AssignedGroupRoleDto,
    CnrDto,
    GroupDto,
    ModelyearDto,
    OptionBasicDtoOptionType,
    RoleDto,
    TypefamilyDto
} from "../../../services/Client";

import Button from "../../../components/Button";
import DeleteButton from "../../../components/DeleteButton";
import AssignedRoleSelection, {RoleAssignmentType} from "./AssignedRoleSelection";
import GroupDetailsDialog from "./GroupDetailsDialog";
import {extractRoles} from "../AdminUtils";
import MultiOptionTypeSelection from "./MultiOptionTypeSelection";
import {ViewAccessRightsDialog} from "./ViewAccessRightsDialog";
import {GroupsUtils} from "./GroupsUtils";

interface IOptionalControls {
    /** Check whether a given DTO has the links to get details. */
    canGetGroupDetails: (dto: GroupDto) => boolean;

    /** Check whether a given DTO has the links to update itself. */
    canUpdateGroup: (dto: GroupDto) => boolean;

    /** Check whether a given DTO has the links to delete itself. */
    canDeleteGroup: (dto: GroupDto) => boolean;
}
export interface IGroupsTableProps extends ITranslatedComponent {
    /** Group objects to display in the table. */
    groups: GroupDto[];

    /** All available roles that may be assigned to the groups. */
    roles: RoleDto[];

    /** All available modelyears that may be assigned to the users. */
    modelyears: ModelyearDto[];

    /** All available typefamilies that may be assigned to the users. */
    typefamiliesMap: ITypefamilyMap;

    /** All available cnrs that may be assigned to the users. */
    cnrs: CnrDto[];

    /** Whether or not the control is waiting for a response from the back end. */
    loading: boolean;

    /** Callbacks to check whether a given DTO has the links for optional features. */
    optionalControls: IOptionalControls;

    /** Callback for when the table want's to update a group. */
    onUpdateGroup: (oldDto: GroupDto, newDto: GroupDto) => any;

    /** Callback for when the table want's to delete a group. */
    onDeleteGroup: (dto: GroupDto) => any;

    /** Flag for displaying/hiding the access view rights for all groups  */
    showAllViewAccessRights: boolean;
}

interface IGroupsTableState {
    /** Whether or not to show the group details dialog. */
    showDetailsDialog?: GroupDto;

    /** Whether or not to show the dialog for changing a groups view access rights. */
    viewAccessRightsGroupDto?: GroupDto;

    /** A set that contains the ids for the groups that have the view access rights visible */
    visibleViewAccessRightsDetails: Set<number>;
}

/**
 * A table-like component that displays a collection of GroupDtos in a
 * structured way and offers actions to manipulate said groups given
 * the necessary privileges/links.
 *
 * The component doesn't manipulate data itself but relies on the client
 * to provide the necessary call backs to update and delete a group.
 */
export class GroupsTable extends React.Component<IGroupsTableProps, IGroupsTableState> {
    constructor(props: IGroupsTableProps) {
        super(props);

        this.state = {
            showDetailsDialog: undefined,
            viewAccessRightsGroupDto: undefined,
            visibleViewAccessRightsDetails: new Set()
        };
    }

    componentDidUpdate(prevProps: IGroupsTableProps) {
        if (prevProps.showAllViewAccessRights !== this.props.showAllViewAccessRights) {
            this.setState({
                visibleViewAccessRightsDetails: this.props.showAllViewAccessRights
                    ? new Set(this.props.groups.map((dto) => dto.objectId))
                    : new Set()
            });
        }
    }

    onUpdateGroup = (modelyears: ModelyearDto[], typefamilies: TypefamilyDto[], cnrs: CnrDto[]) => {
        const {viewAccessRightsGroupDto} = {...this.state};
        const updatedDto = Object.assign({}, viewAccessRightsGroupDto, {
            assignedModelyears: modelyears,
            assignedTypefamilies: typefamilies,
            assignedCnrs: cnrs
        });
        this.setState({viewAccessRightsGroupDto: undefined});
        // @ts-ignore already checked
        this.props.onUpdateGroup(viewAccessRightsGroupDto, updatedDto);
    }

    renderHeader = (): JSX.Element => (
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>{this.props.t("GROUP_NAME")}</Table.HeaderCell>
                <Table.HeaderCell>{this.props.t("ASSIGNED_ROLES")}</Table.HeaderCell>
                <Table.HeaderCell>{this.props.t("ACTIONS")}</Table.HeaderCell>
            </Table.Row>
        </Table.Header>
    )

    renderGroupname = (dto: GroupDto): JSX.Element => (
        <Table.Cell>{dto.groupName}</Table.Cell>
    )

    renderAssignedRoles = (dto: GroupDto): JSX.Element => (
        <div>
            <b>{this.props.t("ROLES")}: </b>
            {this.props.optionalControls.canUpdateGroup(dto) ? (
                <AssignedRoleSelection
                    roles={this.props.roles}
                    roleAssignType={RoleAssignmentType.GROUP}
                    parentDto={dto}
                    values={dto.assignedRoles}
                    onChange={(assignedRoles: AssignedGroupRoleDto[]) => {
                        if (this.props.onUpdateGroup) {
                            const updatedDto = Object.assign({}, dto, {
                                assignedRoles: assignedRoles
                            });
                            this.props.onUpdateGroup(dto, updatedDto);
                        }
                    }}
                />
            ) : (
                dto.assignedRoles &&
                extractRoles(dto.assignedRoles).map((role: RoleDto) => role.name).join(", ")
            )
            }
        </div>
    )

    renderOptionTypeControl = (dto: GroupDto): JSX.Element => (
        <div>
            <b>{this.props.t("OPTION_TYPES")}: </b>
            {this.props.optionalControls.canUpdateGroup(dto) ? (
                <MultiOptionTypeSelection
                    placeholder={this.props.t("SELECT_OPTION_TYPES")}
                    value={dto.optionTypes ?
                        dto.optionTypes.split(',').map((v) => v) as OptionBasicDtoOptionType[] : undefined}
                    onChange={(optionTypes: OptionBasicDtoOptionType[] | undefined) => {
                        if (this.props.onUpdateGroup) {
                            const updatedDto = Object.assign({}, dto, {optionTypes: optionTypes ? _.join(optionTypes, ',') : undefined});
                            this.props.onUpdateGroup(dto, updatedDto);
                        }
                    }}
                />
            ) : (
                dto.optionTypes &&
                dto.optionTypes.split(',').map((oType: OptionBasicDtoOptionType) => this.props.t('GLOBAL:' + oType) + ' (' + oType + ')').join(', ')
            )
            }
        </div>
    )

    renderActions = (dto: GroupDto, viewAccessRightsVisible: boolean, viewAccessRightsExist: boolean): JSX.Element => (
        <Table.Cell>
            {this.props.optionalControls.canDeleteGroup(dto) &&
                <div>
                    <DeleteButton
                        style={{width: '-webkit-fill-available'}}
                        label={this.props.t("DELETE_GROUP")}
                        confirmLabel={this.props.t("CONFIRM")}
                        disabled={this.props.loading}
                        requireConfirmation={true}
                        onClick={() => {
                            if (this.props.onDeleteGroup) {
                                this.props.onDeleteGroup(dto);
                            }
                        }}
                    />
                </div>
            }
            {this.props.optionalControls.canGetGroupDetails(dto) &&
                <div>
                    <Button
                        style={{width: '-webkit-fill-available'}}
                        label={this.props.t("SHOW_DETAILS")}
                        labelPosition="after"
                        icon={"user circle"}
                        onClick={() => this.setState({showDetailsDialog: dto})}
                    />
                </div>
            }
            {this.props.optionalControls.canUpdateGroup(dto) &&
                <>
                    <div>
                        <Button
                            style={{width: '-webkit-fill-available'}}
                            className={viewAccessRightsExist ? 'groups-active-access-rights' : ''}
                            label={this.props.t(viewAccessRightsVisible ? "HIDE_VIEW_ACCESS_RIGHTS" : "SHOW_VIEW_ACCESS_RIGHTS")}
                            labelPosition={"after"}
                            icon={viewAccessRightsVisible ? "hide" : "unhide"}
                            onClick={() => {
                                let visibleViewAccessRightsDetails = new Set(this.state.visibleViewAccessRightsDetails);
                                if (viewAccessRightsVisible) {
                                    visibleViewAccessRightsDetails.delete(dto.objectId);
                                } else {
                                    visibleViewAccessRightsDetails.add(dto.objectId);
                                }

                                this.setState({visibleViewAccessRightsDetails});
                            }}
                        />
                    </div>
                    {viewAccessRightsVisible &&
                        <div>
                            <Button
                                style={{width: '-webkit-fill-available'}}
                                className={viewAccessRightsExist ? 'groups-active-access-rights' : ''}
                                icon={'edit'}
                                label={this.props.t("EDIT_VIEW_ACCESS_RIGHTS")}
                                labelPosition={"after"}
                                onClick={() => this.setState({viewAccessRightsGroupDto: dto})}
                            />
                        </div>
                    }
                </>
            }
        </Table.Cell>
    )

    renderViewAccessRights = (dto: GroupDto): JSX.Element => (
        <>
            {this.props.optionalControls.canUpdateGroup(dto) ?
                this.state.visibleViewAccessRightsDetails.has(dto.objectId) && (
                    <>
                        <Divider horizontal={true}>
                            {this.props.t("VIEW_ACCESS_RIGHTS")}
                        </Divider>

                        {GroupsUtils.renderSelectField(
                            this.props.t("CNRS"),
                            this.props.t("CNR"),
                            this.props.cnrs,
                            dto.assignedCnrs || [],
                            (cnr: CnrDto) => this.props.t(`GLOBAL:${cnr.cnr}`),
                            false,
                            (actual: CnrDto, expected: CnrDto) => actual.objectId === expected.objectId,
                            undefined,
                            undefined,
                            true,
                            true)}

                        {GroupsUtils.renderSelectField(
                            this.props.t("MODELYEARS"),
                            this.props.t("MODELYEAR"),
                            this.props.modelyears,
                            dto.assignedModelyears || [],
                            (modelyear: ModelyearDto) => modelyear.mj4 + '(' + modelyear.mj1 + ')',
                            false,
                            (actual: ModelyearDto, expected: ModelyearDto) => actual.objectId === expected.objectId,
                            undefined,
                            undefined,
                            true,
                            true)}

                        {GroupsUtils.renderSelectField(
                            this.props.t("TYPEFAMILIES"),
                            this.props.t("TYPEFAMILY"),
                            GroupsUtils.getTypefamilyDataSource(this.props.typefamiliesMap, dto.assignedModelyears || []),
                            GroupsUtils.mapAssignedTypefamilies(dto.assignedTypefamilies || []),
                            (tf: String) => this.props.t("GLOBAL:" + tf),
                            false,
                            undefined,
                            undefined,
                            undefined,
                            true,
                            true)}
                    </>
                ) : (
                    <>
                        <div>
                            <b>{this.props.t("CNRS")}: </b>
                            {dto.assignedCnrs && dto.assignedCnrs.map((cnr: CnrDto) => this.props.t(`GLOBAL:${cnr.cnr}`)).join(", ")}
                        </div>
                        <div>
                            <b>{this.props.t("MODELYEARS")}: </b>
                            {dto.assignedModelyears && dto.assignedModelyears.map((modelyear: ModelyearDto) => modelyear.mj4 + '(' + modelyear.mj1 + ')').join(", ")}
                        </div>
                        <div>
                            <b>{this.props.t("TYPEFAMILIES")}: </b>
                            {dto.assignedTypefamilies &&
                                Array.from(new Set(dto.assignedTypefamilies.map((typefamily: TypefamilyDto) => this.props.t("GLOBAL:" + typefamily.key)))).join(", ")}
                        </div>
                    </>
                )}
        </>
    )

    renderRows = (dtos: GroupDto[]): JSX.Element[] => (
        dtos.map((dto, index) => (
            <Table.Row key={index} style={{backgroundColor: index % 2 === 0 ? 'white' : '#80808029'}}>
                {this.renderGroupname(dto)}
                <Table.Cell>
                    {this.renderAssignedRoles(dto)}
                    {this.renderOptionTypeControl(dto)}
                    {this.renderViewAccessRights(dto)}
                </Table.Cell>
                {this.renderActions(
                    dto,
                    this.state.visibleViewAccessRightsDetails.has(dto.objectId),
                    (dto.assignedCnrs !== undefined && dto.assignedCnrs.length > 0) ||
                    (dto.assignedModelyears !== undefined && dto.assignedModelyears.length > 0))
                }
            </Table.Row>
        ))
    )

    renderTable = (dtos: GroupDto[]): JSX.Element => (
        <Table celled={true}>
            {this.renderHeader()}
            <Table.Body>
                {dtos.length > 0
                    ? this.renderRows(dtos)
                    : <Table.Row>
                        <Table.Cell
                            colSpan={'3'}
                            style={{fontWeight: 700, fontSize: 30, color: '#d3d3d3', textAlign: 'center'}}
                        >
                            {this.props.t('GLOBAL:NO_DATA')}
                        </Table.Cell>
                    </Table.Row>}
            </Table.Body>
        </Table>
    )

    render() {
        return (
            <Segment loading={this.props.loading}>
                {this.renderTable(this.props.groups)}

                {this.state.showDetailsDialog &&
                    <GroupDetailsDialog
                        group={this.state.showDetailsDialog}
                        onClose={() => {this.setState({showDetailsDialog: undefined});}}
                    />
                }

                {this.state.viewAccessRightsGroupDto &&
                    <ViewAccessRightsDialog
                        identifier={this.state.viewAccessRightsGroupDto.groupName}
                        assignedModelyears={this.state.viewAccessRightsGroupDto.assignedModelyears || []}
                        assignedTypefamilies={this.state.viewAccessRightsGroupDto.assignedTypefamilies || []}
                        assignedCnrs={this.state.viewAccessRightsGroupDto.assignedCnrs || []}
                        modelyears={this.props.modelyears}
                        typefamiliesMap={this.props.typefamiliesMap}
                        cnrs={this.props.cnrs}
                        t={this.props.t}
                        onClose={() => this.setState({viewAccessRightsGroupDto: undefined})}
                        onSave={this.onUpdateGroup}
                    />
                }
            </Segment>
        );
    }
}

export default translate("ADMIN_VIEW")(GroupsTable);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/GroupsTable.tsx